import { ApiClient, ApiError, type Application, type ApplicationUpsert } from '../lib/api.js';
import { el } from '../lib/dom.js';

export interface ApplicationsAdminDeps {
  api: ApiClient;
  /** Called after any create/update/delete so the shell can refresh. */
  onChanged: () => void;
}

/** Opens the "Manage applications" modal. Returns a dismiss function. */
export function openApplicationsAdmin(deps: ApplicationsAdminDeps): () => void {
  const backdrop = el('div', { class: 'modal-backdrop' });
  const body = el('div', { class: 'app-admin-body' });
  const closeBtn = el('button', { class: 'ghost', text: 'Close' });
  const addBtn = el('button', { class: 'primary', text: '+ Add application' });
  const modal = el('div', {
    class: 'modal modal-wide',
    children: [
      el('header', {
        children: [
          el('div', { class: 'modal-title', children: [el('h3', { text: 'Applications' })] }),
          el('div', { class: 'log-actions', children: [addBtn, closeBtn] }),
        ],
      }),
      body,
    ],
  });
  backdrop.append(modal);
  document.body.append(backdrop);

  const dismiss = () => {
    backdrop.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') dismiss(); };
  document.addEventListener('keydown', onKey);
  closeBtn.addEventListener('click', dismiss);
  backdrop.addEventListener('click', (ev) => { if (ev.target === backdrop) dismiss(); });

  const refresh = async () => {
    body.replaceChildren(el('div', { class: 'empty', text: 'Loading…' }));
    try {
      const { applications } = await deps.api.listApplications();
      renderList(applications);
    } catch (err) {
      body.replaceChildren(
        el('div', { class: 'alert', text: err instanceof ApiError ? err.message : 'Failed to load applications' }),
      );
    }
  };

  const renderList = (apps: Application[]) => {
    const rows = apps.map((a) => renderRow(a));
    body.replaceChildren(...rows);
  };

  const renderRow = (a: Application): HTMLElement => {
    const badges: HTMLElement[] = [];
    if (a.isBuiltin) badges.push(el('span', { class: 'pill idle', text: 'built-in' }));
    if (a.capabilities.runpod) badges.push(el('span', { class: 'pill running', text: 'RunPod' }));
    if (a.capabilities.identityAdmin) badges.push(el('span', { class: 'pill running', text: 'Accounts' }));
    if (!a.githubConfigured) {
      badges.push(el('span', {
        class: 'pill warning',
        text: `token ${a.github.tokenRef} missing`,
        attrs: { title: `Set the ${a.github.tokenRef} env var on the deploy-app and restart it.` },
      }));
    }

    const editBtn = el('button', { text: 'Edit', on: { click: () => openForm(a) } });
    const actions: HTMLElement[] = [editBtn];
    if (!a.isBuiltin) {
      actions.push(el('button', {
        class: 'danger',
        text: 'Delete',
        on: { click: () => void remove(a) },
      }));
    }

    return el('div', {
      class: 'volume-row',
      children: [
        el('div', {
          class: 'volume-head',
          children: [
            el('span', { class: 'volume-name', text: a.name }),
            el('span', { class: 'who', children: badges }),
          ],
        }),
        el('div', {
          class: 'volume-meta',
          text: `${a.github.owner}/${a.github.repo} · ${a.deploy.projectDir} · @${a.deploy.branch}`,
        }),
        el('div', { class: 'volume-actions', children: actions }),
      ],
    });
  };

  const remove = async (a: Application) => {
    if (!confirm(`Delete application "${a.name}"?\n\nThis removes its deploy history and logs. The repo and its files on disk are NOT touched.`)) return;
    try {
      await deps.api.deleteApplication(a.id);
      deps.onChanged();
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Delete failed');
    }
  };

  // ── Add / edit form ────────────────────────────────────────────
  const openForm = (existing: Application | null) => {
    const isEdit = existing !== null;
    const field = (label: string, input: HTMLElement, hint?: string) =>
      el('label', { class: 'form-field', children: [
        el('span', { class: 'form-label', text: label }),
        input,
        hint ? el('span', { class: 'form-hint', text: hint }) : null,
      ] });

    const inp = (value: string, placeholder = '', attrs: Record<string, string> = {}) =>
      el('input', { attrs: { type: 'text', value, placeholder, autocapitalize: 'off', autocorrect: 'off', spellcheck: false, ...attrs } }) as HTMLInputElement;

    const nameI = inp(existing?.name ?? '', 'My App');
    const slugI = inp(existing?.slug ?? '', 'my-app');
    const ownerI = inp(existing?.github.owner ?? 'henrihanot', 'owner');
    const repoI = inp(existing?.github.repo ?? '', 'repo');
    const tokenRefI = inp(existing?.github.tokenRef ?? '', 'MYAPP_GITHUB_TOKEN');
    const protI = inp(existing?.github.protectedBranch ?? 'main', 'main');
    const dirI = inp(existing?.deploy.projectDir ?? '', '/opt/my-app');
    const branchI = inp(existing?.deploy.branch ?? 'main', 'main');

    const proxyOn = el('input', { attrs: { type: 'checkbox', ...(existing?.proxy ? { checked: '' } : {}) } }) as HTMLInputElement;
    const pProject = inp(existing?.proxy?.composeProject ?? '', 'compose project');
    const pService = inp(existing?.proxy?.composeService ?? 'proxy', 'proxy');
    const pPort = inp(existing?.proxy ? String(existing.proxy.internalPort) : '3000', '3000', { type: 'number' });
    const pToken = inp(existing?.proxy?.metricsTokenRef ?? '', 'MYAPP_METRICS_TOKEN');
    const pNet = inp(existing?.proxy?.network ?? '', 'traefik-public');
    const proxyFields = el('div', { class: 'proxy-fields', children: [
      field('Compose project', pProject),
      field('Compose service', pService),
      field('Internal port', pPort),
      field('Metrics token env var', pToken, 'Optional — name of the env var holding the proxy METRICS_TOKEN.'),
      field('Network', pNet),
    ] });
    const syncProxy = () => { proxyFields.style.display = proxyOn.checked ? '' : 'none'; };
    proxyOn.addEventListener('change', syncProxy);
    syncProxy();

    const errBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });
    const saveBtn = el('button', { class: 'primary', text: isEdit ? 'Save changes' : 'Create' });
    const cancelBtn = el('button', { class: 'ghost', text: 'Cancel' });

    const form = el('div', { class: 'app-form', children: [
      errBox,
      field('Display name', nameI),
      isEdit ? null : field('Slug', slugI, 'Stable id — lowercase letters, digits, dashes. Leave blank to derive from the name.'),
      el('div', { class: 'form-section', text: 'GitHub' }),
      field('Owner', ownerI),
      field('Repo', repoI),
      field('Token env var', tokenRefI, 'Name of the env var on THIS deploy-app holding the PAT (e.g. MYAPP_GITHUB_TOKEN). Add it to the deploy-app env and restart it.'),
      field('Protected branch', protI),
      el('div', { class: 'form-section', text: 'Deploy' }),
      field('Project dir', dirI, 'Absolute path under the mounted apps root (e.g. /opt/my-app).'),
      field('Default branch', branchI),
      el('div', { class: 'form-section', children: [
        el('label', { class: 'inline-check', children: [proxyOn, el('span', { text: ' Proxy-replica monitoring' })] }),
      ] }),
      proxyFields,
      el('div', { class: 'form-actions', children: [saveBtn, cancelBtn] }),
    ] });

    body.replaceChildren(form);

    cancelBtn.addEventListener('click', () => { void refresh(); });
    saveBtn.addEventListener('click', async () => {
      errBox.setAttribute('hidden', '');
      const payload: ApplicationUpsert = {
        name: nameI.value.trim(),
        github: {
          owner: ownerI.value.trim(),
          repo: repoI.value.trim(),
          tokenRef: tokenRefI.value.trim(),
          protectedBranch: protI.value.trim() || 'main',
        },
        deploy: {
          projectDir: dirI.value.trim(),
          branch: branchI.value.trim() || 'main',
        },
        proxy: proxyOn.checked
          ? {
              composeProject: pProject.value.trim(),
              composeService: pService.value.trim() || 'proxy',
              internalPort: Number(pPort.value) || 3000,
              metricsTokenRef: pToken.value.trim() || null,
              network: pNet.value.trim() || null,
            }
          : null,
      };
      if (!isEdit && slugI.value.trim()) payload.slug = slugI.value.trim();
      saveBtn.setAttribute('disabled', '');
      try {
        if (isEdit) await deps.api.updateApplication(existing!.id, payload);
        else await deps.api.createApplication(payload);
        deps.onChanged();
        await refresh();
      } catch (err) {
        saveBtn.removeAttribute('disabled');
        errBox.textContent = err instanceof ApiError ? err.message : 'Save failed';
        errBox.removeAttribute('hidden');
      }
    });
  };

  addBtn.addEventListener('click', () => openForm(null));
  void refresh();

  return dismiss;
}
